import { Close, FileCopyOutlined, FlipToBackOutlined, MoreHoriz, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { FormControl, IconButton, InputLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef, useState } from 'react';
import { usePages } from '../../../hooks/usePages';
import { PageData } from '../../../interfaces/interfaces';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { VyConfirm } from '../../common/VyConfirm';
import VySelect from '../../common/VySelect';

interface TemplateControlMenuInterface {
  isVisible: boolean;
  onCopy: (pageid: string) => void;
  onMoveOut: (pageid: string) => void;
  onToggle: () => void;
}

const TemplateControlMenu = ({ isVisible, onCopy, onMoveOut, onToggle }: TemplateControlMenuInterface) => {
  const pagebuilder = useSelector((state) => state.common.pagebuilder);
  const pages = useSelector((state) => state.common.pages);
  const pagedata = useSelector((state) => state.common.pagedata);

  const [isMove, setIsMove] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pageToCopy, setPageToCopy] = useState<PageData | null>(null);
  const [pageToMove, setPageToMove] = useState<PageData | null>(null);
  const [filteredPages, setFilteredPages] = useState<PageData[]>(pages);

  const copybtnref = useRef<HTMLButtonElement>(null);
  const movebtnref = useRef<HTMLButtonElement>(null);

  const { t } = useTranslation();
  const { filterWebPages } = usePages();

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsCopy(false);
    setIsMove(false);
  };

  useEffect(() => {
    if (pagedata?.team) return;
    setFilteredPages(filterWebPages(pages));
  }, [pagedata]);

  return (
    <div>
      <Button
        aria-controls={open ? 'control-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        size="small"
        variant="contained"
        sx={{ background: 'transparent', ':hover': { backgroundColor: 'var(--vystem-blue)' }, padding: 0 }}
        disableElevation>
        <MoreHoriz fontSize="small" />
      </Button>
      <Menu
        id="control-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'control-button',
        }}>
        {!isCopy ? (
          <MenuItem className="mt-2" onClick={() => setIsCopy(true)}>
            <span className="flex flex-row items-center space-x-2">
              <FileCopyOutlined />
              <p>{t('pagebuilder:copy-component-title')}</p>
            </span>
          </MenuItem>
        ) : (
          <div className="flex flex-row justify-center items-end space-x-2 pt-2">
            <div style={{ width: '200px', borderRadius: '4px' }}>
              <FormControl fullWidth>
                <InputLabel style={{ top: -10 }} id="copy-select-label">
                  {t('pagebuilder:copy-select-label')}
                </InputLabel>
                <VySelect
                  fullWidth
                  labelId="copy-select-label"
                  label={t('pagebuilder:copy-select-label')}
                  sx={{ height: '32px' }}
                  onChange={(e) => {
                    setPageToCopy(filteredPages?.find((p) => p._id === e.target.value) || null);
                    copybtnref.current?.click();
                  }}>
                  {filteredPages?.map((p) => (
                    <MenuItem value={p._id}>{p.name}</MenuItem>
                  ))}
                </VySelect>
              </FormControl>
              <div hidden>
                <VyConfirm
                  title={t('pagebuilder:copy-component-title')}
                  onConfirm={() => {
                    pageToCopy && onCopy(pageToCopy?._id);
                    setIsCopy(false);
                    handleClose();
                  }}>
                  <Button ref={copybtnref}></Button>
                </VyConfirm>
              </div>
            </div>
            <IconButton
              size="small"
              color="secondary"
              sx={{ backgroundColor: 'white', '&.MuiButtonBase-root:hover': { backgroundColor: 'white' } }}
              onClick={() => setIsCopy(false)}>
              <Close />
            </IconButton>
          </div>
        )}
        {!isMove ? (
          <MenuItem onClick={() => setIsMove(true)}>
            <span className="flex flex-row items-center space-x-2">
              <FlipToBackOutlined />
              <p>{t('pagebuilder:move-component-title')}</p>
            </span>
          </MenuItem>
        ) : (
          <div className="flex flex-row justify-center items-end space-x-2 pt-2">
            <div style={{ width: '200px', borderRadius: '4px' }}>
              <FormControl fullWidth>
                <InputLabel style={{ top: -10 }} id="move-select-label">
                  {t('pagebuilder:move-select-label')}
                </InputLabel>
                <VySelect
                  fullWidth
                  labelId="move-select-label"
                  label={t('pagebuilder:move-select-label')}
                  sx={{ height: '32px' }}
                  onChange={(e) => {
                    setPageToMove(filteredPages?.find((p) => p._id === e.target.value) || null);
                    movebtnref.current?.click();
                  }}>
                  {filteredPages
                    ?.filter((p) => p._id !== pagebuilder.originalpagedata?._id)
                    .map((p) => (
                      <MenuItem value={p._id}>{p.name}</MenuItem>
                    ))}
                </VySelect>
              </FormControl>
              <div hidden>
                <VyConfirm
                  title={t('pagebuilder:move-component-title')}
                  onConfirm={() => {
                    pageToMove && onMoveOut(pageToMove?._id);
                    setIsMove(false);
                    handleClose();
                  }}>
                  <Button ref={movebtnref}></Button>
                </VyConfirm>
              </div>
            </div>

            <IconButton
              size="small"
              color="secondary"
              sx={{ backgroundColor: 'white', '&.MuiButtonBase-root:hover': { backgroundColor: 'white' } }}
              onClick={() => setIsMove(false)}>
              <Close />
            </IconButton>
          </div>
        )}
        <MenuItem onClick={onToggle}>
          {isVisible ? (
            <span className="flex flex-row items-center space-x-2">
              <VisibilityOutlined />
              <p>{t('pagebuilder:hide-component')}</p>
            </span>
          ) : (
            <span className="flex flex-row items-center space-x-2">
              <VisibilityOffOutlined />
              <p>{t('pagebuilder:show-component')}</p>
            </span>
          )}
        </MenuItem>
      </Menu>
    </div>
  );
};
export default TemplateControlMenu;
